// src/scanresult/Summary.js
import React, { useMemo } from "react";
import {   Grid,
  Paper,
  Typography, 
  Button, Box, BarChart } from '@barracuda-internal/bds-core';
import { BotAttackIcon, DDoSAttackIcon, SQLInjectionIcon } from "../utils/icon";
import {SmallSeverityBars} from '../utils/utility';
import { getSeverityText } from '../utils/color';

function getScoreColor(score) {
  if (typeof score !== "number") return "#d32f2f";
  if (score >= 90) return "#2e7d32"; // green
  if (score >= 80) return "#f9a825"; // yellow
  if (score >= 60) return "#fb8c00"; // orange
  return "#d32f2f"; // red
}

export default function Summary({ summary, vulnerabilities, waf_detected, scrollPage }) {
  // Backend payload expected at: state.scanResult.data
  const data = summary || {};

  // If backend not integrated yet (empty data), fall back to mock data so the UI shows example content.
  const hasBackendData = data && Object.keys(data).length > 0;

  const mockData = useMemo(() => ({
    security_posture_score: 90,
    target_domain: 'demo.com',
    scan_completed_at: 'Oct 1, 2025 8:00 AM',
    vulnerabilities: [
      { title: 'SQL injection', severity: 'high', description: 'Hackers can steal, change, or delete data, and in some cases take control of the backend database.', instances: 1 },
      { title: 'Content Security Policy header not set', severity: 'medium', description: 'CSP header missing', instances: 12 },
      { title: 'Cross-domain misconfiguration', severity: 'medium', description: '', instances: 7 },
      { title: 'Missing Anti-clickjacking header', severity: 'medium', description: '', instances: 3 },
      { title: 'Session ID in URL rewrite', severity: 'medium', description: '', instances: 2 },
      { title: 'Vulnerable JS library', severity: 'low', description: '', instances: 1 }
    ]
  }), []);

  const effectiveData = hasBackendData ? data : mockData;

  const score =
    typeof effectiveData.security_posture_score === "number"
      ? effectiveData.security_posture_score
      : typeof effectiveData.score === "number"
      ? effectiveData.score
      : null;

  const targetDomain = effectiveData.target_domain || effectiveData.domain || effectiveData.host || "";
  const completedOn = effectiveData.scan_completed_at || effectiveData.scanned_at || effectiveData.scan_completed_at || "";
  const vulnerabilities_detected = effectiveData?.vulnerabilities_detected || null

  const scoreColor = getScoreColor(score ?? -1);
  // compute proportional bar width based on score (0-100)
  const maxBarWidth = 160; // px for score == 100
  const safeScore = typeof score === 'number' && !Number.isNaN(score) ? Math.max(0, Math.min(100, score)) : 0;
  const barWidth = Math.round((safeScore / 100) * maxBarWidth);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h5">Summary</Typography>
          {/* Top cards */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, minHeight: 120, display: 'flex', flexDirection: 'column', }}>
                <Typography variant="caption" color="text.secondary">
                  Security posture score
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                  <Typography variant="h2" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {typeof score === "number" ? score : "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of 100
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Box sx={{ width: maxBarWidth, height: 21, bgcolor: '#e8e9ebff', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <Box
                      role="progressbar"
                      aria-valuenow={safeScore}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      sx={{
                        width: `${barWidth}px`,
                        height: 21,
                        bgcolor: scoreColor,
                        transition: 'width 300ms ease',
                      }}
                    />
                  </Box>
                </Box>

                {/* helper link */}
                <Box sx={{ mt: 1 }}>
                  <Button bdsType={'interactiveSubtle'} onClick={() => scrollPage(4)}>How is this calculated?</Button>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={2}>
              <Paper sx={{ p: 2, minHeight: 120,  }}>
                <Typography variant="caption" color="text.secondary">
                  Vulnerabilities detected
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                  {vulnerabilities_detected}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, minHeight: 120,  }}>
                <Typography variant="caption" color="text.secondary">
                  WAF Detected
                </Typography>
                
                <Typography variant="h5" sx={{ width: '80%', fontWeight: 700, mt: 1 }}>
                  <b>{waf_detected?.detected ? waf_detected?.firewall: "No WAF Detected"}</b>
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, minHeight: 120,  }}>
                <Typography variant="caption" color="text.secondary">
                  Scan completed on
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {completedOn ? new Date(completedOn).toLocaleString() : "-"}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Row: Vulnerabilities by severity (left) and Top risks (right) */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 560 }}>Vulnerabilities by severity</Typography>
                    <Button bdsType={'interactiveSubtle'} onClick={() => scrollPage(1)}>View all ↓</Button>
                  </Box>
                  {/* removed central square placeholder per request; keep empty flex so column heights match */}
                  <Box sx={{ flex: 1 }} />

                  <Grid container sx={{ mt: 1, textAlign: 'center' }}>
                      <BarChart
                        chartData={{
                          datasets: [
                            {
                              label: 'Sales',
                              data: [
                                (effectiveData.vulnerabilities_by_severity?.[4] ?? 0), // high
                                (effectiveData.vulnerabilities_by_severity?.[3] ?? 0), // medium
                                (effectiveData.vulnerabilities_by_severity?.[2] ?? 0), // low
                                (effectiveData.vulnerabilities_by_severity?.[1] ?? 0), // info
                              ],
                              backgroundColor: [
                                '#d32f2f',
                                '#fb8c00',
                                '#f9a825',
                                '#2e7d32'
                              ], // Bar fill colors (design tokens)
                              borderColor: [
                                '#d32f2f',
                                '#fb8c00',
                                '#f9a825',
                                '#2e7d32  '
                              ], // Bar border colors (design tokens)
                              borderWidth: 2
                            }
                          ], labels: ['High', 'Medium', 'Low', 'Info'],
                        }}
                        chartDimension={{ height: '300px', width: '100%' }}
                        emptyState={{
                          icon: <div data-testid="empty-state" />,
                          text: 'Too full to eat anymore twix'
                        }}
                        hasEntries={true}
                        showFooter={false}
                        barChartOptions={{
                          plugins: { tooltip: { enabled: false } }, scales: {
                            x: {
                              grid: {
                                display: false, // Removes vertical grid lines
                                drawOnChartArea: false // Removes grid lines only within the chart area
                              }
                            },
                            y: {
                              grid: {
                                display: false, // Removes horizontal grid lines
                                drawOnChartArea: false // Removes grid lines only within the chart area
                              }, border: {
                                display: false // This hides the X-axis line
                              }, ticks: {
                                display: false // This hides the Y-axis labels
                              }
                            }
                          }
                        }}
                      />
                  </Grid>

                  {/* <Divider sx={{ my: 1 }} /> */}

                  <Grid container sx={{ mt: 1 }}>
                    <Grid item xs={3}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#d32f2f' }} />
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{summary?.vulnerabilities_by_severity?.[4]}</Typography>
                        </Box>
                        <Typography variant="subtitle2" sx={{ mt: 1, color: '#616161', fontWeight: 700 }}>Severity: high</Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={3}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#fb8c00' }} />
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{summary?.vulnerabilities_by_severity?.[3]}</Typography>
                        </Box>
                        <Typography variant="subtitle2" sx={{ mt: 1, color: '#616161', fontWeight: 700 }}>Severity: medium</Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={3}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1}}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#f9a825' }} />
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{summary?.vulnerabilities_by_severity?.[2]}</Typography>
                        </Box>
                        <Typography variant="subtitle2" sx={{ mt: 1, color: '#616161', fontWeight: 700 }}>Severity: low</Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={3}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#2e7d32' }} />
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{summary?.vulnerabilities_by_severity?.[1]}</Typography>
                        </Box>
                        <Typography variant="subtitle2" sx={{ mt: 1, color: '#616161', fontWeight: 700 }}>Severity: info</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 560 }}>Top risks</Typography>
                    <Button bdsType={'interactiveSubtle'} onClick={() => scrollPage(1)}>View all ↓</Button>
                  </Box>

                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {vulnerabilities.slice(0,6).map((t, i) => (
                      <Box key={t.id ?? i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                        <Box>
                          <Typography>{t.attack_type_title || t.name}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'left', minWidth: 140, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>Severity: {getSeverityText(t.severity_level)}</Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <SmallSeverityBars severity={t.severity_level} />
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                                      {/* show first/top risk detail below the list */}
                    {vulnerabilities && vulnerabilities[0] ? (
                      <Box sx={{ mt: 2 }} style={{ justifyContent: 'flex-start' }}>
                        <Typography sx={{ fontWeight: 700 }}>{vulnerabilities[0].attack_type_title || vulnerabilities[0].name}</Typography>
                        {vulnerabilities[0].description ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} >{vulnerabilities[0].description}</Typography>
                        ) : null}
                      </Box>
                    ) : null}
                </Paper>
              </Grid>
            </Grid>
          </Grid>


          {/* Attack exposure area */}
          { waf_detected?.detected !== true ?
            <Box sx={{ mt: 2 }}>
              <Paper sx={{ p: 2,  backgroundColor: '#fafafa' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Attack exposure</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>There is <b>no Web Application Firewall</b> detected on your domain and your web application is vulnerable to a wide range of common cyberattacks.</Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2,  backgroundColor: '#fafafa' }}>
                      <Typography sx={{ fontWeight: 700, color: '#111' }}><BotAttackIcon size={16}/> Bot attack</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Without a WAF, automated bots can scrape data, attempt brute-force logins, or perform credential stuffing attacks.</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafafa'  }}>
                      <Typography sx={{ fontWeight: 700, color: '#111' }}><DDoSAttackIcon size={16}/>DDoS attack</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Without a WAF, your application may be overwhelmed by malicious traffic, causing outages.</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafafa'  }}>
                      <Typography sx={{ fontWeight: 700, color: '#111' }}><SQLInjectionIcon size={16}/>SQL injection</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Hackers can steal, change, or delete data, and in some cases take control of the backend database.</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
            :
            <Box sx={{ mt: 2 }}>
              <Paper sx={{ p: 2,  backgroundColor: '#fafafa' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>WAF Detected</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>A Web Application Firewall (WAF) has been detected on your domain. This helps protect your web application from common cyberattacks.</Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2,  backgroundColor: '#fafafa' }}>
                      <Typography sx={{ fontWeight: 700, color: '#111' }}><BotAttackIcon size={16}/>WAF Name</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}><b>{waf_detected?.firewall || "Unknown WAF"}</b></Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafafa'  }}>
                      <Typography sx={{ fontWeight: 700, color: '#111' }}><DDoSAttackIcon size={16}/>Manufacturer</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}><b>{waf_detected?.manufacturer || "Unknown Manufacturer"}</b></Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafafa'  }}>
                      <Typography sx={{ fontWeight: 700, color: '#111' }}><SQLInjectionIcon size={16}/>URL</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}><b>{waf_detected?.url || "Unknown URL"}</b></Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          }
    </Box>
  );
}
